import { ColorProps, colors } from '../../const/color';
import { SizeProps } from '../../const/size';
import { font } from '../../const/font';

export type ButtonVariantProps = 'solid' | 'outlined' | 'link' | 'icon';

export const buttonSize: {
  [key in SizeProps]?: {
    height: number;
    fontSize: number;
    lineHeight: number;
    radius: number;
    padding: number[];
  };
} = {
  xs: {
    height: 24,
    fontSize: font.size.xs,
    lineHeight: 16,
    radius: 4,
    padding: [3, 8],
  },
  sm: {
    height: 32,
    fontSize: font.size.sm,
    lineHeight: 16,
    radius: 6,
    padding: [8, 20], // updated the padding as per new design guideliness - CHESPMD-601
  },
  md: {
    height: 40,
    fontSize: font.size.md,
    lineHeight: 22,
    radius: 8,
    padding: [9, 16],
  },
  lg: {
    height: 48,
    fontSize: font.size.md,
    lineHeight: 22,
    radius: 8,
    padding: [13, 20],
  },
  'ico-xs': {
    height: 24,
    fontSize: font.size.xs,
    lineHeight: 16,
    radius: 0,
    padding: [5, 5],
  },
};

interface ButtonVariantStateProps {
  bg: string;
  color: string;
  borderColor?: string;
}

export const buttonVariant: {
  [key in ButtonVariantProps & { border: boolean }]: {
    [key in ColorProps]?: ButtonVariantStateProps & {
      hover: ButtonVariantStateProps;
      disabled: ButtonVariantStateProps;
    };
  };
} = {
  solid: {
    border: true,
    primary: {
      bg: colors.primary,
      color: colors.white,
      borderColor: colors.primary,
      hover: {
        bg: colors.primaryDark,
        color: colors.white,
        borderColor: colors.primaryDark,
      },
      disabled: {
        bg: colors.grayMedium,
        color: colors.white,
        borderColor: colors.grayMedium,
      },
    },
    primaryLight: {
      bg: colors.primaryLight,
      color: colors.white,
      borderColor: colors.primaryLight,
      hover: {
        bg: colors.primary,
        color: colors.white,
        borderColor: colors.primary,
      },
      disabled: {
        bg: colors.grayMedium,
        color: colors.white,
        borderColor: colors.grayMedium,
      },
    },
    secondary: {
      bg: colors.secondary,
      color: colors.white,
      borderColor: colors.secondary,
      hover: {
        bg: colors.secondaryDark,
        color: colors.white,
        borderColor: colors.secondaryDark,
      },
      disabled: {
        bg: colors.grayMedium,
        color: colors.white,
        borderColor: colors.grayMedium,
      },
    },
    white: {
      bg: colors.white,
      color: colors.primary,
      borderColor: colors.white,
      hover: {
        bg: colors.grayMedium,
        color: colors.primary,
        borderColor: colors.grayMedium,
      },
      disabled: {
        bg: colors.grayMedium,
        color: colors.grayDarker,
        borderColor: colors.grayMedium,
      },
    },
    red: {
      bg: colors.error,
      color: colors.white,
      borderColor: colors.error,
      hover: {
        bg: colors.white,
        color: colors.error,
        borderColor: colors.error,
      },
      disabled: {
        bg: colors.grayMedium,
        color: colors.white,
        borderColor: colors.grayMedium,
      },
    },
    error: {
      bg: colors.error,
      color: colors.white,
      borderColor: colors.error,
      hover: {
        bg: colors.errorLight,
        color: colors.black,
        borderColor: colors.errorLight,
      },
      disabled: {
        bg: colors.grayMedium,
        color: colors.white,
        borderColor: colors.grayMedium,
      },
    },
  },
  outlined: {
    border: true,
    primary: {
      bg: colors.transparent,
      color: colors.primary,
      borderColor: colors.primary,
      hover: {
        bg: colors.primary,
        color: colors.white,
        borderColor: colors.primary,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayMedium,
        borderColor: colors.grayMedium,
      },
    },
    primaryLight: {
      bg: colors.transparent,
      color: colors.primaryLight,
      borderColor: colors.primaryLight,
      hover: {
        bg: colors.primaryLight,
        color: colors.white,
        borderColor: colors.primaryLight,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayMedium,
        borderColor: colors.grayMedium,
      },
    },
    secondary: {
      bg: colors.transparent,
      color: colors.secondary,
      borderColor: colors.secondary,
      hover: {
        bg: colors.secondary,
        color: colors.white,
        borderColor: colors.secondary,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayMedium,
        borderColor: colors.grayMedium,
      },
    },
    grayDarker: {
      bg: colors.transparent,
      color: colors.grayDarker,
      borderColor: colors.grayDarker,
      hover: {
        bg: colors.transparent,
        color: colors.black,
        borderColor: colors.black,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayMedium,
        borderColor: colors.grayMedium,
      },
    },
  },
  link: {
    border: false,
    primary: {
      bg: colors.transparent,
      color: colors.primary,
      hover: {
        bg: colors.transparent,
        color: colors.primaryDark,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayDark,
      },
    },
    primaryLight: {
      bg: colors.transparent,
      color: colors.primaryLight,
      hover: {
        bg: colors.transparent,
        color: colors.primaryDark,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayDark,
      },
    },
    secondary: {
      bg: colors.transparent,
      color: colors.secondary,
      hover: {
        bg: colors.transparent,
        color: colors.secondaryDark,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayMedium,
      },
    },
    grayDarker: {
      bg: colors.transparent,
      color: colors.grayDarker,
      hover: {
        bg: colors.transparent,
        color: colors.black,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayMedium,
      },
    },
    white: {
      bg: colors.transparent,
      color: colors.white,
      hover: {
        bg: colors.transparent,
        color: colors.grayLighter,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.grayDarker,
      },
    },
    tertiaryBlue: {
      bg: colors.transparent,
      color: colors.tertiaryBlue,
      hover: {
        bg: colors.transparent,
        color: colors.tertiaryBlue,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.tertiaryBlue,
      },
    },
  },
  icon: {
    border: false,
    primary: {
      bg: colors.transparent,
      color: colors.primary,
      hover: {
        bg: colors.transparent,
        color: colors.primary,
      },
      disabled: {
        bg: colors.transparent,
        color: colors.primary,
      },
    },
  },
};
